/**
 * Drawer Header Component
 * Part of POV-0040: Drawer container component
 * 
 * Displays part identifier, revision, status badge, and primary actions.
 */
import React from 'react';
import { cn } from '../../lib/utils';

// Status badge styling
const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800 border-green-200',
  draft: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  released: 'bg-blue-100 text-blue-800 border-blue-200',
  obsolete: 'bg-slate-100 text-slate-600 border-slate-200',
  archived: 'bg-red-100 text-red-800 border-red-200',
  deleted: 'bg-red-100 text-red-800 border-red-200',
};

const DrawerHeader = ({
  partNumber,
  partName,
  revision,
  status = 'active',
  isLoading = false,
  onEdit,
  onSave,
  onCancel,
  isEditing = false,
  isSaving = false,
  isDirty = false,
}) => {
  if (isLoading) {
    return (
      <div className="animate-pulse" data-testid="drawer-header-skeleton">
        <div className="h-6 bg-slate-200 rounded w-48 mb-2" />
        <div className="h-4 bg-slate-100 rounded w-64 mb-3" />
        <div className="flex gap-2">
          <div className="h-6 bg-slate-100 rounded w-16" />
          <div className="h-6 bg-slate-100 rounded w-20" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="drawer-header">
      {/* Part Number */}
      <div className="flex items-center gap-3 mb-1">
        <h2 className="text-xl font-bold text-slate-900 font-mono tracking-wide">
          {partNumber || '—'}
        </h2>
        {revision && (
          <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 rounded">
            Rev {revision}
          </span>
        )}
        <span 
          className={cn(
            "px-2 py-0.5 text-xs font-medium rounded border capitalize",
            STATUS_STYLES[status] || STATUS_STYLES.active
          )}
        >
          {status}
        </span>
      </div>

      {/* Part Name */}
      <p className="text-sm text-slate-500 mb-3 line-clamp-2">
        {partName || 'Untitled Part'}
      </p>

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50"
              data-testid="cancel-edit-button"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={isSaving || !isDirty}
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                isDirty
                  ? "bg-haden-primary text-white hover:bg-haden-primary/90"
                  : "bg-slate-100 text-slate-400 cursor-not-allowed"
              )}
              data-testid="save-button"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </>
        ) : (
          <button
            onClick={onEdit}
            className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
            data-testid="edit-button"
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
};

export default DrawerHeader;
